import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Grid,
	MenuItem,
	Paper,
	Typography,
} from "@mui/material";

import { IVFormErros, useVForm, VForm, VTextField } from "../../../shared/forms";
import { VMaskTextField } from "../../../shared/forms/VMaskTextField";
import { BasicModal } from "../../../shared/components/VModal/VModal";
import { FerramentaDetalhe, ModalContext } from "../../../shared/components";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { useNavigate, useParams } from "react-router-dom";
import { Email, } from "@mui/icons-material";
import * as yup from 'yup';
import { useToast } from '../../../shared/hooks';
import { maskPhone, sleep } from '../../../shared/utils';
import { useProfile } from '../../../context/ProfileContext';
import { ToastContent } from '../../../shared/components';
import { useDebounce } from "../../../shared/hooks";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from '@mui/icons-material/Close';
import { Logs } from "../../../shared/interfaces";
import { useAuthContext } from "../../../context";
import moment from "moment";
import { ContractFileUpload } from "./ContractFileUpload";
import { contractService, IDetalheContract, TContractPromise } from "../../../shared/services/api/contract/contractService";
import { isEmpty } from "lodash";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";

interface IFormData {
    title: string;
    description: string;
    platform: string;
    statusContract?: string;
}

const getValidationSchema = (isNew: boolean): yup.SchemaOf<IFormData> => {
    return yup.object().shape({
        title: yup.string().required('Campo Obrigatório'),
        description: yup.string().required('Campo Obrigatório'),
        platform: yup.string().required('Campo Obrigatório'),
        statusContract: isNew
            ? yup.string().optional().notRequired().default("")
            : yup
                    .string()
                    .oneOf(["active", "inactive"], "Campo status é obrigatório selecionar!")
                    .required("Campo status é obrigatório selecionar!"),
    });
};

export const DetalheContratosPresentation: React.FC = () => {
    const { id = 'novo' } = useParams<'id'>();
    const navigate = useNavigate();
    const { formRef, save, saveAndClose, saveAndNew, issaveAndClose } = useVForm();
    const [isLoading, setIsLoading] = useState(false);
    const [title, setTitle] = useState('');
    const [row, setRow] = useState<IDetalheContract>();
    const toastHandler = useToast();
    const [file, setFile] = useState<File>();
    const { debounce } = useDebounce();
    const { isSuperAdmin, permissionLevel, permissions } = useProfile();
    const { loggedUser } = useAuthContext();
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [fileUploadedSuccessfully, setFileUploadedSuccessfully] = useState<boolean>(false);
	const [fileError, setFileError] = useState<string | null>(null);

    const hasContractsOptionsModule = useMemo(() => {
        return Object.prototype.hasOwnProperty.call(permissions ?? {}, PermissionRolesEnum.ContractsOptions);
    }, [permissions]);

    const canAddContract = useMemo(() => {
        if (hasContractsOptionsModule) {
            return checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsAdd);
        }
        return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ContractsAdd);
    }, [hasContractsOptionsModule]);

    const canUpdateContract = useMemo(() => {
        if (hasContractsOptionsModule) {
            return checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsUpdate);
        }
        return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ContractsUpdate);
    }, [hasContractsOptionsModule]);
    const canSave = id === "novo" ? canAddContract : canUpdateContract;
    const isNew = id === "novo";

    useEffect(() => {
        if (id !== 'novo') {
            setIsLoading(true);

            contractService.getByID(id)
                .then((result: IDetalheContract | Error) => {
                    setIsLoading(false);

                    if (result instanceof Error) {
                        const titleMessage = "Alerta!!";
                        const contentMessage = "Erro: " + result?.message;

                        toastHandler.present({
                            type: 'error',
                            position: 'top-right',
                            messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
                        })
                    } else {
                        setTitle(result.title);

                        formRef.current?.setData({
                            ...result,
                        });

                        setRow(result);
                    }
                }).catch((err: any) => {
                    setIsLoading(false);
                    const { titleMessage, contentMessage } = returnMessageError(err);

                    toastHandler.present({
                        type: 'error',
                        position: 'top-right',
                        messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
                    })
                });
        } else {
            setRow(undefined);
            formRef.current?.setData({
                title: '',
                description: '',
                platform: '',
                statusContract: '',
            });
        }
    }, [id]);



    const handleSave = (dados: IFormData) => {
        formRef.current?.setErrors({});
        setFileError(null);

        const missingFile = !file && id == "novo";
        if (missingFile) {
            setFileError("Enviar arquivo do contrato é obrigatório!");
        }

        const schema = getValidationSchema(isNew);
        schema
            .validate(dados, { abortEarly: false })
            .then((dadosvalidados: any) => {
				setIsLoading(true);

                if (missingFile) {
                    setIsLoading(false);
                    toastHandler.present({
                        type: 'error',
                        position: 'top-right',
                        messageOrContent: <ToastContent title={"Alerta!!"} content={"Enviar arquivo do contrato é obrigatório!"} />
                    });
                    return;
                }

                if (id === 'novo') {
                    contractService
                        .create(dadosvalidados, file)
                        .then(async (result: TContractPromise) => {
                            setIsLoading(false);

                            if (result instanceof Error) {
                                const titleMessage = "Alerta!!";
                                const contentMessage = "Erro: " + result?.message;

                                toastHandler.present({
                                    type: 'error',
                                    position: 'top-right',
                                    messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
                                })
                            } else {

                                setFileUploadedSuccessfully(true);
                                setTimeout(() => {
                                    setFileUploadedSuccessfully(false);
                                }, 250);

                                toastHandler.present({
                                    type: 'success',
                                    position: 'top-right',
                                    messageOrContent: <ToastContent title={"Sucesso"} content={"Contrato cadastrado com sucesso."} />
                                })

                                await sleep(3000);

                                if (issaveAndClose()) {
                                    navigate('/clientes/contratos')
                                } else {
                                    navigate(`/clientes/contratos/detalhe/${result}`);
                                }

                            }

                        }).catch((error: any) => {
                            setIsLoading(false);
                            setIsUploading(false);
                            const { titleMessage, contentMessage } = returnMessageError(error);

                            toastHandler.present({
                                type: 'error',
                                position: 'top-right',
                                messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
                            })
                        });
                } else {
                    contractService
                        .updateById(id, dadosvalidados)
                        .then(async (result: void | Error) => {
                            setIsLoading(false);

                            if (result instanceof Error) {
                                const titleMessage = "Alerta!!";
                                const contentMessage = "Erro: " + result?.message;

                                toastHandler.present({
                                    type: 'error',
                                    position: 'top-right',
                                    messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
                                })
                            } else {

                                toastHandler.present({
                                    type: 'success',
                                    position: 'top-right',
                                    messageOrContent: <ToastContent title={"Sucesso"} content={"Contrato atualizado com sucesso."} />
                                })

                                if (issaveAndClose()) {
                                    navigate('/clientes/contratos')
                                }

                            }

                        }).catch((error) => {
                            setIsLoading(false);
                            setIsUploading(false);

                            const { titleMessage, contentMessage } = returnMessageError(error);
                            toastHandler.present({
                                type: 'error',
                                position: 'top-right',
                                messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
                            })
                        });
                }

            })
            .catch((errors: yup.ValidationError) => {
                setIsUploading(false);
                const validationErros: IVFormErros = {};
                errors.inner.forEach(error => {
                    if (!error.path) return;
                    validationErros[error.path] = error.message;
                });

                formRef.current?.setErrors(
                    validationErros
                );
                setIsLoading(false);
            });
    };

    const onFileUploadHandler = (file: File | undefined) => {
        setFile(file);
		if (file) setFileError(null);
    };

    return (
        <LayoutBaseDePagina
            titulo='Contratos'
            subtitulo={isNew ? 'Cadastro de novo contrato' : `Alteração de contrato [ ${title} ]`}
            barraDeFerramentas={
                <FerramentaDetalhe
                    showButtonNew={false}
                    showBotaoApagar={false}
                    showButtonFollowUp={false}

                    showBotaoSalvar={true}
                    isDisabledBotaoSalvar={!canSave || isLoading}
                    isDisabledBotaoSalvarFechar={!canSave || isLoading}
                    clicarSalvar={save}
                    clicarSalvarFechar={saveAndClose}
                    clicarVoltar={() => navigate('/clientes/contratos')}
                />
            }
        >

            <VForm
                id="formIdSave"
                placeholder=""
                onPointerEnterCapture={() => undefined}
                onPointerLeaveCapture={() => undefined}
                ref={formRef}
                onSubmit={handleSave}
			>
				<Box sx={{ width: "100%", maxWidth: "1300px", mx: 1 }}>
					<Box
						component={Paper}
						variant="elevation"
						sx={{
							mt: 1,
							px: { xs: 1, sm: 3 },
							py: { xs: 2, sm: 3 },
							borderRadius: 2,
							overflow: "hidden",
						}}
					>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography variant="h6" color="primary">
									Informações do contrato
								</Typography>
							</Grid>

							<Grid item xs={12} md={6}>
								<VTextField
									fullWidth
									label="Título"
									name="title"
									disabled={isLoading || !isNew || !canSave}
									onChange={(e) => setTitle(e.target.value)}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<VTextField
									fullWidth
									label="Plataforma"
									name="platform"
									disabled={isLoading || !isNew || !canSave}
									select
								>
									<MenuItem value={""}></MenuItem>
									<MenuItem value={"clicksign"}>Clicksign</MenuItem>
								</VTextField>
							</Grid>

							<Grid item xs={12}>
								<VTextField
									fullWidth
									label="Descrição"
									name="description"
									disabled={isLoading || !isNew || !canSave}
									multiline
									minRows={3}
								/>
							</Grid>

                            <Grid item xs={12} md={6}>
								{!isNew ? (
									<VTextField fullWidth label="Status" name="statusContract" disabled={isLoading || !canSave} select>
										<MenuItem value={"active"}>Ativo</MenuItem>
										<MenuItem value={"inactive"}>Inativo</MenuItem>
									</VTextField>
								) : (
                                    <Box>
                                        <ContractFileUpload onFileUploadHandler={onFileUploadHandler} isLoadingProp={isUploading} clear={fileUploadedSuccessfully} />
                                        {fileError && (
                                            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                                                {fileError}
                                            </Typography>
                                        )}
                                    </Box>
								)}
							</Grid>
						</Grid>
					</Box>
				</Box>
            </VForm>

        </LayoutBaseDePagina >
    );
};